'use client';

import { useState } from 'react';
import type { StaticImageData } from 'next/image';
import Header from '@/components/common/Header';
import FilterBar from './components/FilterBar';
import CaseStudyCard from './components/CaseStudyCard';
import PortfolioInteractive from './components/PortfolioInteractive';
import type { FilterState } from './components/FilterBar';
import { useLanguage } from '@/contexts/LanguageContext';
import healthcareImage from '@/../public/assets/images/healthcare-patient.jpg';
import educationImage from '@/../public/assets/images/education-lms.png';

const PortfolioCaseStudiesPage = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useState<FilterState>({
    industry: 'All Industries',
    projectType: 'All Types',
    companySize: 'All Sizes',
    searchQuery: '',
  });

  const healthcareImageSrc = (healthcareImage as StaticImageData).src;
  const educationImageSrc = (educationImage as StaticImageData).src;

  const caseStudies = [
  {
    id: 2,
    title: "Supply Chain Optimization Platform",
    category: "enterprise",
    industry: "Logistics",
    challenge: "Inefficient inventory management and delayed deliveries",
    solution: "AI-powered supply chain analytics and predictive inventory system",
    results: ["25% inventory reduction", "15% faster delivery times", "30% lower carrying costs"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d4ded8b7-1764004174404.png",
    alt: "Supply chain dashboard with real-time inventory tracking and predictive analytics",
    technologies: ["Python", "TensorFlow", "MongoDB", "Docker"],
    duration: "6 months",
    teamSize: "10 engineers"
  },
  {
    id: 3,
    title: "Customer Experience Platform",
    category: "enterprise",
    industry: "Retail",
    challenge: "Fragmented customer data and poor omnichannel experience",
    solution: "Unified customer data platform with AI-driven personalization",
    results: ["20% increase in customer retention", "18% higher conversion rates", "35% reduction in support costs"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17f6f63a0-1764004174331.png",
    alt: "Customer experience dashboard showing personalized recommendations and engagement metrics",
    technologies: ["React", "Node.js", "Redis", "AWS Lambda"],
    duration: "9 months",
    teamSize: "15 engineers"
  },
  {
    id: 4,
    title: "Financial Analytics Suite",
    category: "enterprise",
    industry: "Finance",
    challenge: "Complex financial reporting and manual data entry",
    solution: "Automated financial reporting and forecasting system",
    results: ["90% reduction in reporting time", "20% more accurate forecasts", "15% improved decision-making"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b05dc16b-1764004174418.png",
    alt: "Financial analytics dashboard with automated reporting and forecasting",
    technologies: ["Python", "Pandas", "PostgreSQL", "D3.js"],
    duration: "10 months",
    teamSize: "14 engineers"
  },
  {
    id: 5,
    title: "Healthcare Data Integration Platform",
    category: "enterprise",
    industry: "Healthcare",
    challenge: "Fragmented medical records and compliance issues",
    solution: "Secure healthcare data integration platform with HIPAA compliance",
    results: ["30% faster patient access", "25% reduction in medical errors", "100% compliance with HIPAA"],
    image: healthcareImageSrc,
    alt: "Doctor using a laptop with digital health data overlays",
    technologies: ["React", "Node.js", "MongoDB", "AWS S3"],
    duration: "12 months",
    teamSize: "16 engineers"
  },
  {
    id: 6,
    title: "E-commerce Platform Redesign",
    category: "enterprise",
    industry: "E-commerce",
    challenge: "Outdated platform causing high cart abandonment",
    solution: "Modern e-commerce platform with AI-driven personalization",
    results: ["45% increase in conversion rates", "20% higher average order value", "30% reduction in cart abandonment"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1af7c2cf3-1764004173538.png",
    alt: "E-commerce platform dashboard with personalized product recommendations and conversion tracking",
    technologies: ["React", "Node.js", "PostgreSQL", "AWS"],
    duration: "6 months",
    teamSize: "10 engineers"
  },
  {
    id: 7,
    title: "Smart City Infrastructure Platform",
    category: "enterprise",
    industry: "Government",
    challenge: "Fragmented city data and poor citizen services",
    solution: "Integrated city data platform with AI-driven citizen services",
    results: ["20% faster citizen service requests", "15% reduction in city operations costs", "30% improvement in citizen satisfaction"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11299dd6c-1764004174344.png",
    alt: "Smart city infrastructure dashboard with integrated data and citizen services",
    technologies: ["React", "Node.js", "PostgreSQL", "AWS"],
    duration: "18 months",
    teamSize: "20 engineers"
  },
  {
    id: 8,
    title: "Energy Management System",
    category: "enterprise",
    industry: "Energy",
    challenge: "Inefficient energy consumption and high operational costs",
    solution: "AI-powered energy management system with real-time optimization",
    results: ["25% reduction in energy consumption", "15% lower operational costs", "30% improvement in energy efficiency"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_107c81654-1764004174629.png",
    alt: "Energy management dashboard with real-time consumption monitoring and optimization",
    technologies: ["Python", "TensorFlow", "PostgreSQL", "Docker"],
    duration: "10 months",
    teamSize: "12 engineers"
  },
  {
    id: 9,
    title: "Education Platform Modernization",
    category: "enterprise",
    industry: "Education",
    challenge: "Outdated learning management system and poor student engagement",
    solution: "Modern LMS with AI-driven personalized learning paths",
    results: ["30% increase in student engagement", "25% improvement in learning outcomes", "15% reduction in administrative workload"],
    image: educationImageSrc,
    alt: "Students in a modern classroom using laptops during an interactive session",
    technologies: ["React", "Node.js", "PostgreSQL", "AWS"],
    duration: "9 months",
    teamSize: "14 engineers"
  },
  {
    id: 10,
    title: "Retail Analytics Platform",
    category: "enterprise",
    industry: "Retail",
    challenge: "Limited insights into customer behavior and inventory performance",
    solution: "Advanced retail analytics platform with predictive modeling",
    results: ["20% increase in sales forecasting accuracy", "15% reduction in stockouts", "30% improvement in inventory turnover"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_119376374-1764004175920.png",
    alt: "Retail analytics dashboard with predictive modeling and inventory performance tracking",
    technologies: ["Python", "TensorFlow", "PostgreSQL", "Docker"],
    duration: "8 months",
    teamSize: "10 engineers"
  }];


  const uniqueIndustries = Array.from(new Set(caseStudies.map((study) => study.industry)));
  const uniqueProjectTypes = Array.from(new Set(caseStudies.map((study) => study.category)));

  const getCompanySizeLabel = (teamSize: string) => {
    const sizeNumber = parseInt(teamSize, 10);
    if (Number.isNaN(sizeNumber)) return 'All Sizes';
    if (sizeNumber <= 10) return 'Small (<=10)';
    if (sizeNumber <= 15) return 'Mid (11-15)';
    return 'Large (>15)';
  };

  const uniqueCompanySizes = Array.from(
    new Set(caseStudies.map((study) => getCompanySizeLabel(study.teamSize)))
  );

  const filteredStudies = caseStudies.filter((study) => {
    const matchesIndustry =
      filters.industry === 'All Industries' || study.industry === filters.industry;

    const matchesProjectType =
      filters.projectType === 'All Types' || study.category === filters.projectType;

    const companySizeLabel = getCompanySizeLabel(study.teamSize);
    const matchesCompanySize =
      filters.companySize === 'All Sizes' || companySizeLabel === filters.companySize;

    const search = filters.searchQuery.trim().toLowerCase();
    const matchesSearch =
      search === '' ||
      [study.title, study.industry, study.solution, study.challenge]
        .join(' ')
        .toLowerCase()
        .includes(search);

    return matchesIndustry && matchesProjectType && matchesCompanySize && matchesSearch;
  });

  return (
    <div data-component-id="src/app/portfolio-case-studies/page.tsx:193:4" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="193" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <Header data-component-id="src/app/portfolio-case-studies/page.tsx:194:6" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="194" data-component-file="page.tsx" data-component-name="Header" data-component-content="%7B%22elementName%22%3A%22Header%22%7D" />
      <section data-component-id="src/app/portfolio-case-studies/page.tsx:195:6" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="195" data-component-file="page.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22relative%20bg-gradient-to-br%20from-primary%20via-secondary%20to-primary%20overflow-hidden%20py-20%22%7D" className="relative bg-gradient-to-br from-primary via-secondary to-primary overflow-hidden py-20">
        <div data-component-id="src/app/portfolio-case-studies/page.tsx:196:8" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="196" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20opacity-10%22%7D" className="absolute inset-0 opacity-10">
          <div data-component-id="src/app/portfolio-case-studies/page.tsx:197:10" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="197" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-20%20left-10%20w-96%20h-96%20bg-accent%20rounded-full%20blur-3xl%20animate-pulse%22%7D" className="absolute top-20 left-10 w-96 h-96 bg-accent rounded-full blur-3xl animate-pulse"></div>
        </div>
        <div data-component-id="src/app/portfolio-case-studies/page.tsx:199:8" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="199" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%20text-center%22%7D" className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 data-component-id="src/app/portfolio-case-studies/page.tsx:200:10" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="200" data-component-file="page.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20lg%3Atext-6xl%20font-bold%20text-white%20mb-6%22%7D" className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('portfolio.hero.title')}
          </h1>
          <p data-component-id="src/app/portfolio-case-studies/page.tsx:203:10" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="203" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20md%3Atext-2xl%20text-white%2F90%20max-w-3xl%20mx-auto%22%7D" className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            {t('portfolio.hero.subtitle')}
          </p>
        </div>
      </section>
      <div data-component-id="src/app/portfolio-case-studies/page.tsx:208:6" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="208" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%20py-16%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div data-component-id="src/app/portfolio-case-studies/page.tsx:209:8" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="209" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-4%22%7D" className="mt-4">
          <FilterBar data-component-id="src/app/portfolio-case-studies/page.tsx:210:10" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="210" data-component-file="page.tsx" data-component-name="FilterBar" data-component-content="%7B%22elementName%22%3A%22FilterBar%22%7D"
            industries={uniqueIndustries}
            projectTypes={uniqueProjectTypes}
            companySizes={uniqueCompanySizes}
            onFilterChange={setFilters}
          />
        </div>

        <div data-component-id="src/app/portfolio-case-studies/page.tsx:218:8" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="218" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-12%20grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-8%22%7D" className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
          {filteredStudies?.map((study) =>
          <CaseStudyCard data-component-id="src/app/portfolio-case-studies/page.tsx:220:10" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="220" data-component-file="page.tsx" data-component-name="CaseStudyCard" data-component-content="%7B%22elementName%22%3A%22CaseStudyCard%22%7D" key={study.id} study={study} />
          )}
        </div>

        <div data-component-id="src/app/portfolio-case-studies/page.tsx:224:8" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="224" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-20%22%7D" className="mt-20">
          <PortfolioInteractive data-component-id="src/app/portfolio-case-studies/page.tsx:225:10" data-component-path="src/app/portfolio-case-studies/page.tsx" data-component-line="225" data-component-file="page.tsx" data-component-name="PortfolioInteractive" data-component-content="%7B%22elementName%22%3A%22PortfolioInteractive%22%7D" />
        </div>
      </div>
    </div>);

};

export default PortfolioCaseStudiesPage;
